// WishFormFields — the five fields a wish is made of, with no form element and
// no submit button around them.
//
// W49.0 §8. `/wishes/new` was the only place these lived, which is why there
// was no way to **edit** any of them: `patchWish` has five call sites and every
// one of them changes `status` and nothing else. A person could mark a wish
// done but could not fix a typo in its name, change its date, or make it
// private — the sheet offered 「編輯」 and it was `disabled` with a hint reading
// 「W33.3 上線」, which was accurate only in the sense that it never did.
//
// The split is deliberate about what does *not* move: the `<form>` element and
// the submit/cancel row stay with each page, because create and edit differ in
// what they submit, where they go afterwards, and whether the action is docked.
// Sharing the fields is the whole requirement; sharing the shell would have
// meant a boolean prop deciding three different navigations.
//
// Copy, chip lists and hints come from `./wishes/wishFormCopy` unchanged — this
// is a move, not a rewrite, so the create page's own tests keep asserting the
// same strings.

import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { FilterChipGroup } from "@/components/primitives/FilterChipGroup";
import { cn } from "@/lib/utils/cn";
import {
  WISH_VISIBILITIES,
  type WishCategory,
  type WishPriority,
  type WishVisibility,
} from "@/lib/api/wishlist";
import {
  CATEGORY_CHIPS,
  CATEGORY_TEMPLATE_HINT,
  PRIORITY_CHIPS,
  VISIBILITY_META,
} from "./wishFormCopy";

export interface WishFormState {
  name: string;
  category: WishCategory;
  priority: WishPriority;
  targetDate: string;
  visibility: WishVisibility;
}

export interface WishFormFieldsProps {
  form: WishFormState;
  onChange: (patch: Partial<WishFormState>) => void;
  /** Name field shows its error state (create: after submit; edit: after a change). */
  nameInvalid: boolean;
  dateInvalid: boolean;
  /** ISO today, for the date field's `min`. */
  today: string;
  /** Server-side failure text, if any. */
  errorMessage?: string | null;
  /** Create focuses the name field; edit must not steal focus on open. */
  autoFocusName?: boolean;
}

export function WishFormFields({
  form,
  onChange,
  nameInvalid,
  dateInvalid,
  today,
  errorMessage,
  autoFocusName = false,
}: WishFormFieldsProps) {
  return (
    <>
      <FormErrorBanner message={errorMessage} />

      <FormField
        name="name"
        label="心願名稱"
        required
        hint="1-100 字（會出現在心願板 + archive 標題）"
        autoFocus={autoFocusName}
        placeholder="例：東京 5 日旅遊"
        maxLength={100}
        minLength={1}
        value={form.name}
        onChange={(next) => onChange({ name: next })}
        invalid={nameInvalid}
        errorMessage={nameInvalid ? "請填寫心願名稱" : undefined}
        data-testid="field-name"
      />

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-semibold text-ink-1">類別</legend>
        <p className="text-body-sm text-ink-3">{CATEGORY_TEMPLATE_HINT[form.category]}</p>
        <FilterChipGroup
          items={CATEGORY_CHIPS}
          selectedId={form.category}
          onSelect={(id) => onChange({ category: id as WishCategory })}
          accent="paper-deep"
          kind="category"
          // Per W35.5 follow-up issue #7: 7 category chips overflow on
          // mobile (393px). Switch from `layout="swipe"` (horizontal
          // scroll — hidden behind chevron-less edge) to `layout="wrap"`
          // (default) which lays out as 2 rows on mobile, all visible.
          layout="wrap"
          ariaLabel="心願類別"
        />
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-semibold text-ink-1">優先度</legend>
        <FilterChipGroup
          items={PRIORITY_CHIPS}
          selectedId={form.priority}
          onSelect={(id) => onChange({ priority: id as WishPriority })}
          accent="ink-1"
          kind="status"
          ariaLabel="心願優先度"
        />
      </fieldset>

      <FormField
        name="target_date"
        label="目標日期"
        type="date"
        hint="可不填；目標日期會帶出 calendar mirror（私人心願不會）"
        min={today}
        value={form.targetDate}
        onChange={(next) => onChange({ targetDate: next })}
        invalid={Boolean(dateInvalid)}
        errorMessage={dateInvalid ? "目標日期不能早於今天" : undefined}
        data-testid="field-target-date"
      />

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-semibold text-ink-1">可見性</legend>
        <div className="flex flex-wrap gap-2">
          {WISH_VISIBILITIES.map((id) => {
            const selected = form.visibility === id;
            const meta = VISIBILITY_META[id];
            const Icon = meta.icon;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange({ visibility: id })}
                data-testid={`visibility-${id}`}
                className={cn(
                  "tap-target press h-11 px-4 rounded-pill inline-flex items-center gap-1.5 text-meta border",
                  selected
                    ? "bg-ink-1 text-paper border-transparent"
                    : "bg-paper-elevated text-ink-1 border-hairline hover:bg-black/5",
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {meta.label}
              </button>
            );
          })}
        </div>
        <p className="text-body-sm text-ink-3">
          {form.visibility === "shared"
            ? "另一半會在心願板看到這筆"
            : "只有自己看得到；不會寫入日曆 mirror"}
        </p>
      </fieldset>
    </>
  );
}
